import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  InputAdornment
} from '@mui/material';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';

function PersonalInstallmentForm({ onInstallmentAdded }) {
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    amountRaw: '',
    category: '',
    installment_count: 2,
    payment_source: ''
  });


  const [categories, setCategories] = useState([]);
  const [paymentSources, setPaymentSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data, error } = await supabase
          .from('personal_categories')
          .select('*')
          .order('name');

        if (error) throw error;
        setCategories(data || []);
      } catch (error) {
        console.error('Erro ao buscar categorias:', error);
        setSnackbar({
          open: true,
          message: 'Erro ao carregar categorias',
          severity: 'error'
        });
      }
    };

    const fetchPaymentSources = async () => {
      try {
        const { data, error } = await supabase
          .from('payment_sources')
          .select('*')
          .eq('is_active', true)
          .order('name');
        if (error) throw error;
        setPaymentSources(data || []);
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Erro ao carregar origens de pagamento',
          severity: 'error'
        });
      }
    };

    fetchCategories();
    fetchPaymentSources();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'payment_source') {
      const found = paymentSources.find(src => src.id === value);
      setSelectedSource(found || null);
    }
  };

  const handleValueChange = (e) => {
    const value = e.target.value;
    // Remove caracteres não numéricos
    const numericValue = value.replace(/\D/g, '');
    // Formata como moeda brasileira
    const formattedValue = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue / 100);

    setFormData(prev => ({
      ...prev,
      amount: formattedValue,
      amountRaw: numericValue / 100
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validação simples
    if (!formData.description.trim() || !formData.amount || !formData.category || !formData.installment_count) {
      setSnackbar({
        open: true,
        message: 'Preencha todos os campos obrigatórios',
        severity: 'error'
      });
      return;
    }

    // Converte o valor formatado para número (igual ao cash-flow-personal)
    const value = parseFloat(formData.amount.replace(/[R$\s.]/g, '').replace(',', '.'));
    if (isNaN(value) || value <= 0) {
      setSnackbar({
        open: true,
        message: 'Valor inválido',
        severity: 'error'
      });
      return;
    }

    const count = parseInt(formData.installment_count, 10);
    if (isNaN(count) || count < 2) {
      setSnackbar({
        open: true,
        message: 'Número de parcelas deve ser 2 ou mais',
        severity: 'error'
      });
      return;
    }

    setLoading(true);

    try {
      const groupId = uuidv4();
      const transactions = [];
      // Cálculo preciso das parcelas
      const valuePerInstallment = Math.floor((value / count) * 100) / 100;
      let soma = 0;

      // Lógica simplificada para calcular as datas das parcelas
      const now = new Date();
      
      for (let i = 0; i < count; i++) {
        let installmentDate;

        if (selectedSource && selectedSource.is_credit_card) {
          // Para cartão de crédito: sempre usar o dia 7 do mês
          // A primeira parcela será no próximo dia 7 disponível
          const currentMonth = now.getMonth();
          const currentYear = now.getFullYear();
          
          // Se hoje for depois do dia 7, a primeira parcela será no próximo mês
          const monthOffset = now.getDate() > 7 ? i + 1 : i;
          
          let targetMonth = currentMonth + monthOffset;
          let targetYear = currentYear;
          
          // Ajustar ano se necessário
          if (targetMonth > 11) {
            targetYear += Math.floor(targetMonth / 12);
            targetMonth = targetMonth % 12;
          }
          
          installmentDate = new Date(targetYear, targetMonth, 7);
        } else {
          // Não é cartão: usa a data atual + i meses
          installmentDate = new Date(now);
          installmentDate.setMonth(installmentDate.getMonth() + i);
        }

        let parcelaValue = valuePerInstallment;
        if (i === count - 1) {
          parcelaValue = parseFloat((value - soma).toFixed(2));
        }
        soma += parcelaValue;
        transactions.push({
          type: 'saida',
          description: formData.description.trim(),
          value: -parcelaValue,
          category_id: formData.category,
          date: installmentDate.toISOString().split('T')[0],
          installment_count: count,
          installment_number: i + 1,
          installment_group_id: groupId,
          payment_source_id: formData.payment_source || null
        });
      }

      const { error } = await supabase
        .from('personal_transactions')
        .insert(transactions);

      if (error) throw error;

      setSnackbar({
        open: true,
        message: 'Parcelamento pessoal criado com sucesso',
        severity: 'success'
      });

      if (onInstallmentAdded) onInstallmentAdded();
      setFormData({
        description: '',
        amount: '',
        amountRaw: '',
        category: '',
        date: new Date().toISOString().split('T')[0],
        installment_count: 2,
        payment_source: ''
      });
      setSelectedSource(null);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Erro ao criar parcelamento pessoal: ' + error.message,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Grid container spacing={5}>
        <Grid item xs={12}>
          <TextField
            label="Descrição"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            fullWidth
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Origem do Pagamento"
            name="payment_source"
            value={formData.payment_source}
            onChange={handleChange}
            required
            fullWidth
          >
            <MenuItem value="">Selecione a origem</MenuItem>
            {paymentSources.map((source) => (
              <MenuItem key={source.id} value={source.id}>
                {source.name} {source.is_credit_card ? '(Cartão)' : ''}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            select
            label="Categoria"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            fullWidth
          >
            <MenuItem value="">Selecione uma categoria</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id}>
                {category.parent_id ? `→ ${category.name}` : category.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Valor Total"
            name="amount"
            value={formData.amount}
            onChange={handleValueChange}
            required
            fullWidth
            InputProps={{
              inputProps: {
                inputMode: 'numeric'
              }
            }}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label="Número de Parcelas"
            name="installment_count"
            type="number"
            value={formData.installment_count}
            onChange={handleChange}
            required
            fullWidth
            inputProps={{ min: 2 }}
          />
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Salvando...' : 'Adicionar Parcelamento'}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default PersonalInstallmentForm;
